import {DOCUMENT} from '@angular/common';
import {Component, Inject, Input, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {HeaderIdentifier} from '@application/headers/header-identifier.enum';
import {PageTypeEnum} from '@application/helper/navigation-helper/page-type.enum';
import {NavigationId} from '@application/helper/routing/navigation-id.enum';
import {NavigationUtils} from '@application/helper/routing/navigation-utils';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {AsyncUniqueValidator} from '@application/validators/async-unique-validator';
import {Customer} from '@domain/customer/customer';
import {IdName} from '@domain/id-name';
import {StandardSpeed} from '@domain/machine/standard-speed.enum';
import {ProductionScheduleStatus} from '@domain/production-schedule/production-schedule-status.enum';
import {IdNameUnitOfMeasurement} from '@domain/texfab/plastic-production-order/id-name-unit-of-measurement';
import {PlasticProductionOrder} from '@domain/texfab/plastic-production-order/plastic-production-order';
import {SpeedAndEfficiency} from '@domain/texfab/plastic-production-order/speed-and-efficiency';
import {PlasticProduct} from '@domain/textile-data/plastic-product/plastic-product';
import {UnitOfMeasurement} from '@domain/textile-data/plastic-product/unit-of-measurement.enum';
import {Tool} from '@domain/utilities/tool/tool';
import {MACHINE_GROUPS, MachineGroups} from '@infrastructure/http/machine-group/machine-groups';
import {MACHINES, Machines} from '@infrastructure/http/machine/machines.interface';
import {HttpPlasticProductionOrdersCustomSettingsService} from '@infrastructure/http/plastic-production-order/http-plastic-production-orders-custom-settings.service';
import {HttpPlasticProductionOrdersService} from '@infrastructure/http/plastic-production-order/http-plastic-production-orders.service';
import {PRODUCTION_SCHEDULES, ProductionSchedules} from '@infrastructure/http/production-schedule/production-schedules';
import {MachineOverviewList} from '@presentation/pages/machine-overview/machine-overview-list';
import {MachineOverviewService} from '@presentation/pages/machine-overview/overview-machines-views/overview-machines-list/machine-overview.service';
import {PlasticProductSelectionService} from '@presentation/pages/textile-data/plastic-product/plastic-product-selection.service';
import {TextileService} from '@presentation/pages/textile-data/textile-data-overview/textile.service';
import {TextileDataType} from '@presentation/pages/textile-data/textile-data-type.enum';
import {MatchingCriteriaOverviewService} from '@presentation/pages/utilities/tool/add/matching-criteria/matching-criteria-overview.service';
import {ToolSelectionService} from '@presentation/pages/utilities/tool/tool-selection.service';
import {
  AssertionUtils,
  AsyncGreaterThanValidator,
  BackendError,
  BaseComponent,
  CanLeavePage,
  convertSpeedAndCycleTime,
  CrudObjectSelectionDialogComponent,
  CrudObjectSelectionDialogInput,
  CrudOverviewDataBuilderFactoryService,
  CrudOverviewDataConfig,
  DialogBuilderFactoryService,
  DialogType,
  FormValidationHelper,
  GridOptionsBuilder,
  NavigationHelperPrototypeService,
  SaveType,
  skeletonViewAnimation,
  TabsMenuItem,
  TranslateService
} from '@vdw/angular-component-library';
import {ColDef} from 'ag-grid-community';
import {DateTime} from 'luxon';
import {finalize, Observable, Subject, switchMap, takeUntil} from 'rxjs';
import {CustomerSelectionService} from '../../customer/customer-selection.service';
import {PlasticProductionOrderCustomSettingsComponent} from '../custom-settings/plastic-production-order-custom-settings.component';

type CrudOverviewDataConfigType = PlasticProduct | MachineOverviewList | Tool | IdName | Customer;

@Component({
  templateUrl: './add-plastic-production-order-page.component.html',
  styleUrls: ['./add-plastic-production-order-page.component.scss'],
  animations: [skeletonViewAnimation('.form-field-skeleton-wrapper, .mat-card-header, .button-skeleton-wrapper')]
})
export class AddPlasticProductionOrderPageComponent extends BaseComponent implements OnInit, CanLeavePage {
  @Input() public pageType: PageTypeEnum;
  @Input() public itemId: number;
  @Input() public canShowHeader: boolean = false;

  public addPlasticProductionOrderGeneralForm: FormGroup<{
    name: FormControl<string>;
    dueDate: FormControl<DateTime>;
    product: FormControl<IdNameUnitOfMeasurement | PlasticProduct>;
    quantity: FormControl<number>;
    machine: FormControl<IdName>;
    machineGroup: FormControl<IdName>;
    tool: FormControl<IdName | Tool>;
    customer: FormControl<IdName | Customer>;
    plannedSpeed: FormControl<number>;
    plannedEfficiency: FormControl<number>;
    releaseForProduction: FormControl<boolean>;
  }>;

  public selectedMenuItem: TabsMenuItem = {value: 0, translationKey: 'GENERAL.GENERAL'};
  public placeholderForSpeed = '0';
  public placeholderForEfficiency = '0';

  private machineConfig: CrudOverviewDataConfig<CrudOverviewDataConfigType>;
  private machineGroupConfig: CrudOverviewDataConfig<CrudOverviewDataConfigType>;

  public readonly SAVE_TYPE = SaveType;
  public readonly HEADER_IDENTIFIER = HeaderIdentifier.ADD_PLASTIC_PRODUCTION_ORDER;
  public readonly MIN_DUE_DATE = DateTime.now();
  public readonly TRANSLATION_KEY = 'PLASTIC_PRODUCTION_ORDER.PLASTIC_PRODUCTION_ORDER';
  public readonly PLASTIC_PRODUCT_ROUTE = RouteUtils.paths.texStyle.plasticProduct.editPlasticProduct.absolutePath;
  public readonly TOOL_ROUTE = RouteUtils.paths.utilities.tool.editTool.absolutePath;

  private plasticProductionOrderId: number;
  protected currentProductionOrder: PlasticProductionOrder;
  protected customSettingsUpdated: Subject<void> = new Subject();

  public constructor(
    @Inject(MACHINES) private readonly machines: Machines,
    @Inject(MACHINE_GROUPS) private readonly machineGroups: MachineGroups,
    @Inject(PRODUCTION_SCHEDULES) private readonly productionSchedules: ProductionSchedules,
    @Inject(DOCUMENT) private readonly document: Document,
    protected readonly navigationHelper: NavigationHelperPrototypeService,
    protected readonly plasticProductionOrdersCustomSettings: HttpPlasticProductionOrdersCustomSettingsService,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly plasticProductionOrders: HttpPlasticProductionOrdersService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly textileService: TextileService,
    private readonly translate: TranslateService,
    private readonly builderFactoryService: CrudOverviewDataBuilderFactoryService<CrudOverviewDataConfigType>,
    private readonly toolSelectionService: ToolSelectionService,
    private readonly customerSelectionService: CustomerSelectionService,
    private readonly plasticProductSelectionService: PlasticProductSelectionService,
    private readonly machineOverviewService: MachineOverviewService,
    private readonly matchingCriteriaOverviewService: MatchingCriteriaOverviewService
  ) {
    super();
    this.plasticProductSelectionService.init(false);
  }

  public get createdObject(): string {
    return !AssertionUtils.isNullOrUndefined(this.addPlasticProductionOrderGeneralForm.controls.name.value) ? this.addPlasticProductionOrderGeneralForm.controls.name.value : '';
  }

  public ngOnInit(): void {
    this.setGeneralFormFields();
    this.setBuilderConfigForMachine();
    this.setBuilderConfigForMachineGroup();

    this.plasticProductionOrderId =
      !AssertionUtils.isNullOrUndefined(this.activatedRoute.snapshot.params.id) && !this.isAddingProductionOrderForMachine() ? Number(this.activatedRoute.snapshot.params.id) : this.itemId;
    this.addPlasticProductionOrderGeneralForm.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe(() => this.setCurrentProductionOrder());

    if (this.plasticProductionOrderId) {
      this.getPlasticProductionOrder();
    }

    if (this.isAddingProductionOrderForMachine()) {
      this.initializeForMachine();
    }
  }

  public canLeavePage(): boolean {
    return !this.addPlasticProductionOrderGeneralForm.dirty || this.saving;
  }

  public save(saveType: SaveType): void {
    const isValid = new FormValidationHelper().checkForms([this.addPlasticProductionOrderGeneralForm], this.document);
    if (isValid) {
      const plasticProductionOrderToSave = this.currentProductionOrder;
      this.saving = true;

      const request: Observable<void | number> = this.isEditingPlasticProductionOrder()
        ? this.plasticProductionOrders.update(plasticProductionOrderToSave)
        : this.plasticProductionOrders.save(plasticProductionOrderToSave);
      request.pipe(takeUntil(this.unSubscribeOnViewDestroy), finalize(this.isEditingPlasticProductionOrder() ? this.finalizeSaving() : null)).subscribe({
        next: (id: number) => {
          if (this.navigationHelper.contentSwitcherDialogActive()) {
            this.navigationHelper.navigateBack();
            return;
          }

          if (this.isEditingPlasticProductionOrder()) {
            this.getPlasticProductionOrder();
          }

          this.textileService.navigateAndShowToast(
            saveType,
            TextileDataType.PLASTIC_PRODUCTION_ORDER,
            this.TRANSLATION_KEY,
            this.isEditingPlasticProductionOrder(),
            plasticProductionOrderToSave.name,
            id
          );
        },
        error: (errorMessage: BackendError) =>
          this.showErrorDialogForBackendError(this.isEditingPlasticProductionOrder() ? 'GENERAL.ACTIONS.EDIT_OBJECT' : 'GENERAL.ACTIONS.CREATE_OBJECT', errorMessage.message)
      });
    }
  }

  public isEditingPlasticProductionOrder(): boolean {
    if (!AssertionUtils.isNullOrUndefined(this.pageType)) {
      return this.pageType === PageTypeEnum.EDIT;
    }
    return this.activatedRoute.snapshot.routeConfig.path === NavigationUtils.getNavigationRoute(NavigationId.EDIT_PLASTIC_ORDER).path;
  }

  public duplicatePlasticProductionOrder(): void {
    this.router.navigateByUrl(RouteUtils.paths.texFab.plastic.duplicateProductionOrder.absolutePath.replace(':id', this.plasticProductionOrderId.toString()));
  }

  public cancel(): void {
    this.navigationHelper.navigateBack();
  }

  public deletePlasticProductionOrder(): void {
    this.textileService.removeConfirmation(this.currentProductionOrder, TextileDataType.PLASTIC_PRODUCTION_ORDER, false, null, this.plasticProductionOrders);
  }

  public selectProduct(): void {
    const field = this.addPlasticProductionOrderGeneralForm.controls.product;

    this.plasticProductSelectionService
      .openContentSwitcherDialog(
        [field.value as PlasticProduct],
        {activeTab: this.selectedMenuItem, product: this.currentProductionOrder},
        this.addPlasticProductionOrderGeneralForm?.controls?.tool?.value?.id,
        this.addPlasticProductionOrderGeneralForm?.controls?.machine?.value?.id,
        this.addPlasticProductionOrderGeneralForm?.controls?.machineGroup?.value?.id
      )
      .subscribe((object: CrudOverviewDataConfigType[]) => {
        if (!AssertionUtils.isNullOrUndefined(object)) {
          field.setValue(object[0] as PlasticProduct);
          this.addPlasticProductionOrderGeneralForm.controls.customer.setValue((object[0] as PlasticProduct)?.customer);
        }
      });
  }

  public selectMachine(): void {
    this.machineConfig.dataObservable = this.machines.getCompatible(
      this.addPlasticProductionOrderGeneralForm?.controls?.tool?.value?.id,
      this.addPlasticProductionOrderGeneralForm?.controls?.product?.value?.id,
      this.addPlasticProductionOrderGeneralForm?.controls?.machineGroup?.value?.id
    );

    this.selectObjectWithNavigationHelper(this.machineConfig, this.addPlasticProductionOrderGeneralForm.controls.machine);
  }

  public selectMachineGroup(): void {
    this.machineGroupConfig.dataObservable = this.machineGroups.getCompatible(
      this.addPlasticProductionOrderGeneralForm?.controls?.product?.value?.id,
      this.addPlasticProductionOrderGeneralForm?.controls?.machine?.value?.id,
      this.addPlasticProductionOrderGeneralForm?.controls?.tool?.value?.id
    );

    this.selectObjectWithNavigationHelper(this.machineGroupConfig, this.addPlasticProductionOrderGeneralForm.controls.machineGroup);
  }

  public selectTool(): void {
    const field = this.addPlasticProductionOrderGeneralForm.controls.tool;

    this.toolSelectionService
      .openContentSwitcherDialog(
        field.value as Tool,
        this.addPlasticProductionOrderGeneralForm?.controls?.product?.value?.id,
        this.addPlasticProductionOrderGeneralForm?.controls?.machine?.value?.id,
        this.addPlasticProductionOrderGeneralForm?.controls?.machineGroup?.value?.id
      )
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((tool: CrudOverviewDataConfigType[]) => {
        if (!AssertionUtils.isNullOrUndefined(tool)) {
          field.setValue(tool[0] as Tool);
        }
      });
  }

  public selectCustomer(): void {
    const field = this.addPlasticProductionOrderGeneralForm.controls.customer;

    this.customerSelectionService
      .openContentSwitcherDialog(field.value as Customer)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((customer: CrudOverviewDataConfigType[]) => {
        if (!AssertionUtils.isNullOrUndefined(customer)) {
          field.setValue(customer[0] as Customer);
        }
      });
  }

  public getQuantitySuffixTranslationKey(): string {
    const product = this.addPlasticProductionOrderGeneralForm.value.product;
    return `TEXTILE_DATA.PLASTIC_PRODUCT.UNIT_OF_MEASUREMENT.${product?.unitOfMeasurement ?? UnitOfMeasurement.PIECES}`;
  }

  public getProductionOrderStatus(): string {
    return this.isEditingPlasticProductionOrder() && this.currentProductionOrder?.status
      ? this.translate.instant('PRODUCTION_ORDER.STATUS.' + this.currentProductionOrder.status.toString())
      : this.translate.instant('PRODUCTION_ORDER.STATUS.CREATING');
  }

  protected applyNameSuggestion(suggestion: string): void {
    this.addPlasticProductionOrderGeneralForm.controls.name.patchValue(suggestion);
  }

  protected openCustomSettings(): void {
    this.navigationHelper
      .navigateWithContentSwitcher({
        componentInfo: {component: PlasticProductionOrderCustomSettingsComponent}
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(() => this.customSettingsUpdated.next());
  }

  private setCurrentProductionOrder(): void {
    const product = this.addPlasticProductionOrderGeneralForm.controls.product.value;
    const tool = this.addPlasticProductionOrderGeneralForm.controls.tool.value;
    const customer = this.addPlasticProductionOrderGeneralForm.controls.customer.value;

    this.currentProductionOrder = new PlasticProductionOrder(
      this.isEditingPlasticProductionOrder() ? this.plasticProductionOrderId : null,
      this.addPlasticProductionOrderGeneralForm.controls.name.value,
      this.currentProductionOrder?.status ?? ProductionScheduleStatus.CREATING,
      this.currentProductionOrder?.state,
      this.addPlasticProductionOrderGeneralForm.value.dueDate ? this.addPlasticProductionOrderGeneralForm.controls.dueDate.value.toJSDate() : null,
      product ? new IdNameUnitOfMeasurement(product.id, product.name, product.unitOfMeasurement, product.speedUnit) : null,
      this.addPlasticProductionOrderGeneralForm.controls.quantity.value,
      this.addPlasticProductionOrderGeneralForm.controls.machine.value,
      this.addPlasticProductionOrderGeneralForm.controls.machineGroup.value,
      tool ? new IdName(tool.id, tool.name) : null,
      customer ? new IdName(customer.id, customer.name) : null,
      this.addPlasticProductionOrderGeneralForm.controls.plannedSpeed.value,
      this.addPlasticProductionOrderGeneralForm.controls.plannedEfficiency.value,
      this.addPlasticProductionOrderGeneralForm.controls.releaseForProduction.value
    );
  }

  private setGeneralFormFields(): void {
    this.addPlasticProductionOrderGeneralForm = this.formBuilder.group({
      name: this.formBuilder.control(null, Validators.required, AsyncUniqueValidator.createValidator(this.productionSchedules, null)),
      dueDate: this.formBuilder.control(null),
      product: this.formBuilder.control(null, Validators.required),
      quantity: this.formBuilder.control({value: null, disabled: true}, [Validators.required, Validators.min(1)]),
      machine: this.formBuilder.control(null),
      machineGroup: this.formBuilder.control(null),
      tool: this.formBuilder.control(null),
      customer: this.formBuilder.control(null),
      plannedSpeed: this.formBuilder.control(null, [], AsyncGreaterThanValidator.createGreaterThanValidator(0)),
      plannedEfficiency: this.formBuilder.control(null, Validators.max(100), AsyncGreaterThanValidator.createGreaterThanValidator(0)),
      releaseForProduction: this.formBuilder.control(false)
    });

    this.addPlasticProductionOrderGeneralForm.controls.product.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((product: PlasticProduct) => {
      if (AssertionUtils.isNullOrUndefined(product)) {
        this.addPlasticProductionOrderGeneralForm.controls.quantity.setValue(null);
        this.addPlasticProductionOrderGeneralForm.controls.quantity.disable();
      } else {
        this.addPlasticProductionOrderGeneralForm.controls.quantity.enable();
      }
    });

    this.selectedMenuItem.form = this.addPlasticProductionOrderGeneralForm;

    this.getPlaceholderForSpeedAndEfficiency('product');
    this.getPlaceholderForSpeedAndEfficiency('machine');
    this.getPlaceholderForSpeedAndEfficiency('tool');
  }

  private setFormValues(plasticProductionOrder: PlasticProductionOrder): void {
    this.addPlasticProductionOrderGeneralForm.reset({
      name: this.isDuplicatingPlasticProductionOrder() ? null : plasticProductionOrder.name,
      dueDate: plasticProductionOrder.dueDate ? DateTime.fromJSDate(plasticProductionOrder.dueDate) : null,
      product: plasticProductionOrder.plasticProduct,
      quantity: plasticProductionOrder.plannedQuantity,
      machine: plasticProductionOrder.machine,
      machineGroup: plasticProductionOrder.machineGroup,
      tool: plasticProductionOrder.tool,
      customer: plasticProductionOrder.customer,
      plannedSpeed: plasticProductionOrder.plannedSpeed,
      plannedEfficiency: plasticProductionOrder.plannedEfficiency,
      releaseForProduction: plasticProductionOrder.releaseForProduction
    });

    if (this.isEditingPlasticProductionOrder()) {
      this.addPlasticProductionOrderGeneralForm.controls.name.setAsyncValidators([AsyncUniqueValidator.createValidator(this.productionSchedules, plasticProductionOrder.name)]);
      this.addPlasticProductionOrderGeneralForm.controls.name.updateValueAndValidity();
    }

    if (this.isEditingPlasticProductionOrder() && !plasticProductionOrder.canEdit()) {
      this.addPlasticProductionOrderGeneralForm.disable();
    } else if (!AssertionUtils.isNullOrUndefined(plasticProductionOrder.dueDate) && plasticProductionOrder.dueDate <= this.MIN_DUE_DATE.toJSDate()) {
      this.addPlasticProductionOrderGeneralForm.controls.dueDate.setValue(null);
    }
  }

  private isDuplicatingPlasticProductionOrder(): boolean {
    if (!AssertionUtils.isNullOrUndefined(this.pageType)) {
      return this.pageType === PageTypeEnum.DUPLICATE;
    }
    return this.activatedRoute.snapshot.routeConfig.path === NavigationUtils.getNavigationData(NavigationId.DUPLICATE_PLASTIC_ORDER).route;
  }

  private showErrorDialogForBackendError(translationKey: string, message: string): void {
    this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
      titleText: this.translate.instant(translationKey, {object: this.translate.instant(this.TRANSLATION_KEY)}),
      messageText: message,
      type: DialogType.INFORMATION
    });
  }

  private setBuilderConfigForMachine(): void {
    this.machineConfig = this.getConfig(
      this.translate.instant('MACHINE.MACHINE', {count: 2}),
      this.machineOverviewService.getColumnDefsForSpeed(),
      GridIdentifier.SELECT_MACHINE,
      (builder: GridOptionsBuilder) => this.machineOverviewService.getDefaultGridOptionsBuilder(builder)
    );
  }

  private setBuilderConfigForMachineGroup(): void {
    this.machineGroupConfig = this.getConfig(
      this.translate.instant('PLANNING.MACHINE_GROUP'),
      this.matchingCriteriaOverviewService.getColDefForMachineGroup(),
      GridIdentifier.SELECT_MACHINE_GROUP,
      (builder: GridOptionsBuilder) => this.machineOverviewService.getDefaultGridOptionsBuilder(builder)
    );
  }

  private getConfig(
    translationKey: string,
    colDefs: ColDef[],
    gridIdentifier: string,
    configureBuilder?: (builder: GridOptionsBuilder) => GridOptionsBuilder
  ): CrudOverviewDataConfig<CrudOverviewDataConfigType> {
    let builderFactoryService = this.builderFactoryService
      .getBuilder()
      .withEntityName(translationKey)
      .withGridOptions(colDefs, gridIdentifier, null, (builder: GridOptionsBuilder) => configureBuilder(builder).withRowSelection(false, false, true).build());

    return builderFactoryService.build();
  }

  private selectObjectWithNavigationHelper(config: CrudOverviewDataConfig<CrudOverviewDataConfigType>, field: FormControl<IdName>): void {
    config.selectedObjects = [field.value];

    this.navigationHelper
      .navigateWithContentSwitcher<CrudOverviewDataConfigType[]>({
        componentInfo: {component: CrudObjectSelectionDialogComponent, componentInputs: {config} as CrudObjectSelectionDialogInput}
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((object: CrudOverviewDataConfigType[]) => {
        if (!AssertionUtils.isEmpty(object)) {
          field.setValue(new IdName(object[0].id, object[0].name));
        }
      });
  }

  private getPlaceholderForSpeedAndEfficiency(fieldName: string): void {
    this.addPlasticProductionOrderGeneralForm.controls[fieldName].valueChanges
      .pipe(
        takeUntil(this.unSubscribeOnViewDestroy),
        switchMap(() =>
          this.plasticProductionOrders.getSpeedAndEfficiency(
            this.addPlasticProductionOrderGeneralForm.controls.product.value?.id,
            this.addPlasticProductionOrderGeneralForm.controls.machine.value?.id,
            this.addPlasticProductionOrderGeneralForm.controls.tool.value?.id
          )
        )
      )
      .subscribe((speedEfficiency: SpeedAndEfficiency) => {
        this.placeholderForSpeed = AssertionUtils.isNullOrUndefined(speedEfficiency.plannedSpeed) ? null : String(this.getFormattedPlannedSpeed(speedEfficiency.plannedSpeed));
        this.placeholderForEfficiency = AssertionUtils.isNullOrUndefined(speedEfficiency.plannedEfficiency) ? null : String(speedEfficiency.plannedEfficiency);
      });
  }

  private getFormattedPlannedSpeed(plannedSpeed: number): number {
    return this.addPlasticProductionOrderGeneralForm.controls?.product.value?.speedUnit === StandardSpeed.SPEED ? convertSpeedAndCycleTime(plannedSpeed) : plannedSpeed;
  }

  private getPlasticProductionOrder(): void {
    this.plasticProductionOrders
      .getById(this.plasticProductionOrderId)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((plasticProductionOrder: PlasticProductionOrder) => {
        this.setFormValues(plasticProductionOrder);
        this.currentProductionOrder.status = plasticProductionOrder.status;
        this.currentProductionOrder.state = plasticProductionOrder.state;
      });
  }

  private isAddingProductionOrderForMachine(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.navigationHelper.getCurrentEntryData<IdName>()?.id);
  }

  private initializeForMachine(): void {
    const foundMachine = this.navigationHelper.getCurrentEntryData<IdName>();

    const machineControl = this.addPlasticProductionOrderGeneralForm.controls.machine;
    machineControl.setValue(foundMachine);
    machineControl.disable();

    this.navigationHelper.currentEntry = null;
  }
}
